export default class Clustering {
	constructor({
		id = null,
		name = "",
		total_clusters = 3,
		total_micro_clusters = null,
		points = [],
		clusters = [],
		micro_clusters = [],
		created_at = null,
		updated_at = null,
		company_id = null,
		cluster_config = [],
	} = {}) {
		this.id = id;
		this.name = name;
		this.total_clusters = total_clusters;
		this.total_micro_clusters = total_micro_clusters;
		this.points = points;
		this.clusters = clusters;
		this.micro_clusters = micro_clusters;
		this.created_at = created_at;
		this.updated_at = updated_at;
		this.company_id = company_id;
		this.cluster_config = cluster_config;
	}

	/**
	 * Parse API response to Clustering model
	 * API returns array of micro_clusters
	 */
	static fromApiResponse(apiData) {
		if (!apiData || !Array.isArray(apiData)) {
			return new Clustering();
		}

		// Group micro_clusters by cluster_id
		const clustersMap = {};
		const allPoints = [];
		const microClusters = apiData;

		microClusters.forEach((mc) => {
			// Group by cluster_identification instead of cluster_id
			const clusterKey = mc.cluster_identification;

			if (!clustersMap[clusterKey]) {
				clustersMap[clusterKey] = {
					id: mc.cluster_id, // Keep original ID reference if needed, or use identification
					identification: mc.cluster_identification,
					micro_clusters: [],
					points: [],
					color: mc.cluster_color, // Use first micro_cluster color
				};
			}

			// Add micro_cluster
			clustersMap[clusterKey].micro_clusters.push(mc);

			// Add points with cluster and micro_cluster info
			mc.points.forEach((point) => {
				const enhancedPoint = {
					...point,
					cluster_id: mc.cluster_id,
					cluster_identification: mc.cluster_identification,
					micro_cluster_id: mc.micro_cluster_identification,
					cluster_color: mc.cluster_color,
					latitude: point.latitude,
					longitude: point.longitude,
				};
				clustersMap[clusterKey].points.push(enhancedPoint);
				allPoints.push(enhancedPoint);
			});
		});

		return new Clustering({
			clusters: Object.values(clustersMap),
			micro_clusters: microClusters,
			points: allPoints,
			total_clusters: Object.keys(clustersMap).length,
			total_micro_clusters: microClusters.length,
		});
	}

	/**
	 * Parse API response to Micro Clustering model (WITHOUT grouping)
	 * Each micro_cluster is treated as an independent cluster
	 * API returns array of micro_clusters
	 */
	static fromApiResponseMicroClusters(apiData) {
		if (!apiData || !Array.isArray(apiData)) {
			return new Clustering();
		}

		const microClusters = apiData;
		const allPoints = [];

		// Process each micro_cluster and extract its points
		microClusters.forEach((mc) => {
			// Add points with micro_cluster info
			if (mc.points && Array.isArray(mc.points)) {
				mc.points.forEach((point) => {
					const enhancedPoint = {
						...point,
						cluster_id: mc.cluster_id,
						cluster_identification: mc.cluster_identification,
						micro_cluster_id: mc.micro_cluster_identification,
						cluster_color: mc.cluster_color,
						latitude: point.latitude,
						longitude: point.longitude,
					};
					allPoints.push(enhancedPoint);
				});
			}
		});

		// Each micro_cluster is treated as an independent cluster
		return new Clustering({
			clusters: microClusters, // No grouping - each MC is a cluster
			micro_clusters: microClusters,
			points: allPoints,
			total_clusters: microClusters.length,
			total_micro_clusters: microClusters.length,
		});
	}

	/**
	 * Convert model to API payload format
	 */
	toPayload() {
		const payload = {
			name: this.name,
			total_clusters: parseInt(this.total_clusters),
			points: this.points.map((p) => ({
				identification: p.identification,
				latitude: p.cy || p.latitude,
				longitude: p.cx || p.longitude,
				...(p.demand > 0 && {demand: p.demand}),
			})),
		};

		if (this.id) {
			payload.id = this.id;
		}

		if (this.total_micro_clusters !== null && this.total_micro_clusters !== undefined) {
			payload.total_micro_clusters = parseInt(this.total_micro_clusters);
		}

		if (this.company_id) {
			payload.company_id = this.company_id;
		}

		if (this.cluster_config && this.cluster_config.length > 0) {
			payload.cluster_config = this.cluster_config;
		}

		return payload;
	}
	/**
	 * Payload to fetch pagination
	 */
	payloadToFetchPagination() {
		return [
			"id",
			"name",
			"company_id",
			"success",
			"status",
			"added_on",
			"total_points",
			"total_clusters",
			"total_micro_clusters",
		];
	}
	/**
	 * Fetch filter pagination
	 */
	toFetchFilterPagination(data) {
		const {page, quantity, fields, sort, filters, order_by, order_sort, logical_operator, match_filter} = data;

		const payload = {
			page: page ? page : 1,
			quantity: quantity ? quantity : 100000,
			fields: fields && fields.length > 0 ? fields : this.payloadToFetchPagination(),
			match_filter: match_filter !== undefined ? match_filter : true,
			logical_operator: logical_operator !== undefined ? logical_operator : "and",
		};

		if (sort && sort.length > 0) {
			payload.sort = sort;
		}

		if (order_by && order_by.length > 0) {
			payload.order_by = order_by;
		}
		// add all of properties of filters object to payload

		if (order_sort && Object.keys(order_sort).length > 0) {
			payload.order_sort = order_sort;
		}

		if (filters && Object.keys(filters).length > 0) {
			Object.keys(filters).forEach((key) => {
				payload[key] = filters[key];
			});
		}
		return payload;
	}
	/**
	 * Validate if clustering has minimum required data
	 */
	isValid() {
		return this.name && this.name.length > 0 && this.total_clusters > 0 && this.points.length > 0;
	}

	/**
	 * Get total points count
	 */
	getTotalPoints() {
		return this.points.length;
	}

	/**
	 * Get total micro clusters count
	 */
	getTotalMicroClusters() {
		return this.micro_clusters.length;
	}

	/**
	 * Get clusters grouped by cluster_id
	 */
	getGroupedClusters() {
		return this.clusters;
	}

	/**
	 * Get all micro clusters
	 */
	getMicroClusters() {
		return this.micro_clusters;
	}

	/**
	 * Check if clustering has been saved (has ID)
	 */
	isPersisted() {
		return this.id !== null && this.id !== undefined;
	}

	/**
	 * Get statistics for a specific cluster
	 */
	getClusterStats(clusterId) {
		const cluster = this.clusters.find((c) => c.cluster_identification === clusterId);
		if (!cluster) return null;

		return {
			id: cluster.id,
			identification: cluster.identification,
			total_points: cluster.points.length,
			total_micro_clusters: cluster.micro_clusters.length,
			micro_clusters: cluster.micro_clusters.map((mc) => ({
				id: mc.micro_cluster_identification,
				color: mc.cluster_color,
				total_points: mc.total_points,
				total_demand: mc.total_demand,
				total_capacity: mc.total_capacity,
				usage_percentage: mc.usage_percentage,
				centroid: mc.centroid,
			})),
		};
	}
}
